import { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import SoftButton from "components/SoftButton";
import SoftBox from "components/SoftBox";
import FileDropzone from "../FileDropzone";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { uploadFile } from "helpers/common.js";
import { updateQuery } from "api/UdasWrapper";
import { TenantContext } from "context/AppProvider/TenantContext";
import { UserContext } from "context/AppProvider/UserContext";
import { setUsers } from "redux/actions/portal/usersActionCreator";

function UploadModal({
  openUploadModal,
  setOpenUploadModal,
  pathname,
  setCompanyLogo,
  setUserAvatar,
  setLogoKey,
  targetUser,
  targetTenant
}) {
  const { user } = useAuth0();
  const [file, setFile] = useState(null);
  const { setTenantData, setTenantLogoKey } = useContext(TenantContext);
  const { setUserData } = useContext(UserContext);
  const destinationPath = pathname === "/administration/company" ? "logo" : "avatar";
  const [isUploading, setIsUploading] = useState(false);
  const { users } = useSelector((store) => store.portal.users);
  const dispatch = useDispatch();

  const getPathAuth0Id = () => {
    const match = pathname?.match(/\/account\/users\/profile-overview\/(.+)$/);
    return match?.[1] ? decodeURIComponent(match[1]) : "";
  };

  const getMutationResult = (response, fieldName) => {
    if (response?.data?.[fieldName]) return response.data[fieldName];
    if (response?.[fieldName]) return response[fieldName];
    return null;
  };

  const send = async () => {
    try {
      if (isUploading || !file) return;

      setIsUploading(true);

      const pathAuth0Id = getPathAuth0Id();
      const uploadTargetUser = targetUser || {};
      const uploadTargetTenant = targetTenant || {};

      const uploadAuth0Id =
        uploadTargetUser?.auth0_id ||
        pathAuth0Id ||
        user?.cxfUserId ||
        "";

      const uploadTenantId =
        uploadTargetUser?.tenant_id ||
        uploadTargetTenant?.tenant_id ||
        user?.stripeId ||
        "";

      const response = await uploadFile(user, file, destinationPath);

      if (!response?.Location) {
        setIsUploading(false);
        toast.error("Image upload failed");
        return;
      }

      const timeNow = Date.now();
      const imageUrl = `${response.Location}?timestamp=${timeNow}`;

      if (destinationPath === "avatar") {
        const data = JSON.stringify({
          query: `mutation UpdateUserImage($auth0_id: String!, $tenant_id: String!, $image: String!) {
            updateUserImage(auth0_id: $auth0_id, tenant_id: $tenant_id, image: $image) {
              isSuccessful
              message
              resultObject
            }
          }`,
          variables: {
            auth0_id: `${uploadAuth0Id}`,
            tenant_id: `${uploadTenantId}`,
            image: imageUrl
          }
        });

        const updateImageResponse = await updateQuery(data);
        const mutationResult = getMutationResult(updateImageResponse, "updateUserImage");

        if (!mutationResult?.isSuccessful) {
          setIsUploading(false);
          toast.error(mutationResult?.message || "Image failed to be stored in the database");
          return;
        }

        setUserAvatar(imageUrl);

        if (!pathAuth0Id || uploadAuth0Id === user?.cxfUserId) {
          setUserData((prev) => ({ ...prev, image: imageUrl }));
        }

        dispatch(
          setUsers(
            users?.map((u) => (u.auth0_id === uploadAuth0Id ? { ...u, image: imageUrl } : u))
          )
        );
      } else {
        const data = JSON.stringify({
          query: `mutation UpdateCompanyLogo($tenant_id: String!, $image: String!) {
            updateCompanyLogo(tenant_id: $tenant_id, image: $image) {
              isSuccessful
              message
              resultObject
            }
          }`,
          variables: {
            tenant_id: `${uploadTenantId}`,
            image: imageUrl
          }
        });

        const updateImageResponse = await updateQuery(data);
        const mutationResult = getMutationResult(updateImageResponse, "updateCompanyLogo");

        if (!mutationResult?.isSuccessful) {
          setIsUploading(false);
          toast.error(mutationResult?.message || "Image failed to be stored in the database");
          return;
        }

        setTenantLogoKey(response.Etag);
        if (setLogoKey) {
          setLogoKey(response.Etag);
        }

        setCompanyLogo(imageUrl);
        setTenantData((prev) => ({
          ...prev,
          logo_url: imageUrl
        }));
      }

      setFile(null);
      setOpenUploadModal(false);
      setIsUploading(false);
    } catch (error) {
      console.log(error);
      setIsUploading(false);
      toast.error("Image upload failed");
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    setOpenUploadModal(false);
    setFile(null);
  };

  useEffect(() => {
    if (openUploadModal) {
      setTimeout(() => {
        const fileUpload = document.getElementById("file");
        if (fileUpload) {
          fileUpload.setAttribute("accept", "image/*");
        }
      }, 500);
    }
  }, [openUploadModal]);

  return (
    <Dialog open={openUploadModal} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Upload a new {pathname === "/administration/company" ? "company picture" : "profile picture"}
      </DialogTitle>

      <DialogContent>
        {file ? (
          <SoftBox
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2
            }}
          >
            <SoftBox
              component="img"
              src={URL.createObjectURL(file)}
              width="200px"
              height="200px"
              sx={{ objectFit: "contain" }}
            />
            <SoftButton
              color="secondary"
              onClick={() =>
              !isUploading &&
              setTimeout(() => {
                setFile(null);
              }, 500)
              }
              size="small"
              >
              Remove
            </SoftButton>
          </SoftBox>
        ) : (
          <FileDropzone setFile={setFile} />
        )}
      </DialogContent>

      <DialogActions>
        <SoftButton variant="outlined" color="info" onClick={handleClose}>
          cancel
        </SoftButton>
        <SoftButton disabled={!file || isUploading} color="info" onClick={send}>
          {isUploading && <CircularProgress sx={{ color: "#FFF", marginRight: "8px" }} size={14} />}
          {isUploading ? "Uploading" : "Upload"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

UploadModal.defaultProps = {
  openUploadModal: false
};

UploadModal.propTypes = {
  openUploadModal: PropTypes.bool,
  setOpenUploadModal: PropTypes.func
};

export default UploadModal;